// KaziPeople Music — Home. The product surface: a warm hero, the user's Recently Played
// (private, provider-sourced), Company Mix + Active Challenges (KaziPeople's workplace layer),
// mood shortcuts into Search, and what's Trending at the company. Provider-neutral; personal
// data stays client-side. Designed to feel alive even before there's much company data.
import React, { useEffect, useMemo, useState } from 'react';
import { Play, Trophy, Users, Clock, Music2, Flame, ChevronRight, ListMusic, ExternalLink, Sparkles, Search as SearchIcon, Plus, X } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { useAppSelector } from '@/app/hooks';
import { openProviderUrl } from '@/lib/providerLinks';
import { appleMusicProvider } from './lib/appleMusicProvider';
import { usePlayer } from './player/usePlayer';
import { useMusicConnected } from './lib/useMusicConnected';
import type { MusicItem } from './lib/types';
import * as api from '@/services/performanceService';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
type MusicChallenge = api.MusicChallenge;
type MusicSubmission = api.MusicSubmission;

const provider = appleMusicProvider();
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');

// Best-effort Apple Music song id from a canonical URL (for queueing the mix). Songs use
// ?i=<id>; direct song URLs end in the id. Non-Apple tracks return null (deep-link only).
function appleSongId(url: string): string | null {
  try {
    const u = new URL(url);
    if (!/(^|\.)music\.apple\.com$/.test(u.hostname)) return null;
    const i = u.searchParams.get('i');
    if (i) return i;
    const parts = u.pathname.split('/').filter(Boolean);
    return parts.includes('song') ? parts[parts.length - 1] || null : null;
  } catch { return null; }
}
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

// Mood shortcuts — seed a catalog search. Purely a discovery convenience; no listening data.
// User-customizable: each viewer can add/remove their own moods (saved in the browser).
type Mood = { label: string; query: string };
const DEFAULT_MOODS: Mood[] = [
  { label: 'Focus', query: 'focus instrumental' },
  { label: 'Energy', query: 'workout energy' },
  { label: 'Chill', query: 'chill lofi' },
  { label: 'Afrobeats', query: 'afrobeats' },
  { label: 'Amapiano', query: 'amapiano' },
  { label: 'Feel good', query: 'feel good hits' },
];
const MOODS_KEY = 'kp-music-moods';
const MOOD_GRADIENTS = [
  'from-sky-500/15 to-sky-500/5 text-sky-600 dark:text-sky-300',
  'from-orange-500/15 to-orange-500/5 text-orange-600 dark:text-orange-300',
  'from-violet-500/15 to-violet-500/5 text-violet-600 dark:text-violet-300',
  'from-emerald-500/15 to-emerald-500/5 text-emerald-600 dark:text-emerald-300',
  'from-rose-500/15 to-rose-500/5 text-rose-600 dark:text-rose-300',
  'from-amber-500/15 to-amber-500/5 text-amber-600 dark:text-amber-300',
  'from-cyan-500/15 to-cyan-500/5 text-cyan-600 dark:text-cyan-300',
  'from-fuchsia-500/15 to-fuchsia-500/5 text-fuchsia-600 dark:text-fuchsia-300',
];
const gradientFor = (s: string) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return MOOD_GRADIENTS[h % MOOD_GRADIENTS.length]; };
const loadMoods = (): Mood[] => {
  try { const raw = localStorage.getItem(MOODS_KEY); if (raw) { const m = JSON.parse(raw); if (Array.isArray(m) && m.length) return m; } } catch { /* ignore */ }
  return DEFAULT_MOODS;
};

export const MusicHome: React.FC<{
  onOpenChallenges: () => void;
  onOpenChallenge: (c: MusicChallenge) => void;
  onOpenSearch?: (seed?: string) => void;
}> = ({ onOpenChallenges, onOpenChallenge, onOpenSearch }) => {
  const { playItem, setQueue } = usePlayer();
  const user = useAppSelector((s) => (s as any).auth?.user) as any;
  const name = user?.firstName || user?.first_name || (user?.name || user?.fullName || '').split(' ')[0] || 'there';
  const { connected } = useMusicConnected();

  const [recent, setRecent] = useState<MusicItem[]>([]);
  const [challenges, setChallenges] = useState<MusicChallenge[]>([]);
  const [trending, setTrending] = useState<MusicSubmission[]>([]);
  const [mix, setMix] = useState<api.CompanyMix | null>(null);
  const [showMixList, setShowMixList] = useState(false);
  const [moods, setMoods] = useState<Mood[]>(() => loadMoods());
  const [editMoods, setEditMoods] = useState(false);
  const persistMoods = (next: Mood[]) => { setMoods(next); try { localStorage.setItem(MOODS_KEY, JSON.stringify(next)); } catch { /* ignore */ } };
  const promptText = usePrompt();
  const addMood = async () => {
    const name = (await promptText({ title: 'Add a genre or mood', label: 'Genre or mood', placeholder: 'e.g. Amapiano, Gqom, Bongo Flava', required: true, confirmLabel: 'Add' }))?.trim();
    if (!name) return;
    if (moods.some((m) => m.label.toLowerCase() === name.toLowerCase())) return;
    persistMoods([...moods, { label: name, query: name }]);
  };
  const removeMood = (label: string) => persistMoods(moods.filter((m) => m.label !== label));

  useEffect(() => { if (connected) provider.getRecentlyPlayed().then(setRecent).catch(() => setRecent([])); }, [connected]);
  useEffect(() => {
    api.listMusicChallenges().then(setChallenges).catch(() => setChallenges([]));
    api.musicTrending().then(setTrending).catch(() => setTrending([]));
    api.getCompanyMix().then(setMix).catch(() => setMix(null));
  }, []);

  // Dedupe recently played — providers often repeat the same album/track back-to-back
  // (the "Michael Jackson × 5" effect). Keep first occurrence by id, then title+artist.
  const recentUnique = useMemo(() => {
    const seen = new Set<string>();
    return recent.filter((i) => {
      const key = i.id || `${i.title}·${i.artist || i.subtitle || ''}`.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [recent]);

  const mixItems = useMemo<MusicItem[]>(() => (mix?.tracks || [])
    .map((t) => { const id = appleSongId(t.canonical_url); return id ? { kind: 'song' as const, id, provider: 'apple_music' as const, title: t.title } : null; })
    .filter(Boolean) as MusicItem[], [mix]);

  const playMix = () => {
    if (mixItems.length) void setQueue(mixItems);
    else if (mix?.tracks[0]) openProviderUrl(mix.tracks[0].canonical_url);
  };

  const featured = recentUnique[0];
  const active = challenges.filter((c) => c.status === 'OPEN');
  const openSearch = (seed?: string) => onOpenSearch?.(seed);

  return (
    <div className="space-y-8">
      {/* Hero — warm, on-brand, always present. Doubles as the primary "play" moment. */}
      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-500 to-amber-500 p-6 text-white sm:p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-black/10 blur-2xl" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xl">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> KaziPeople Music
            </div>
            <h2 className="text-2xl font-bold sm:text-3xl">{greeting()}, {name} 👋</h2>
            <p className="mt-1 text-sm text-white/85">Discover music, build the vibe with your team, and jump into challenges.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {mix && mix.tracks.length > 0 && (
                <Button size="sm" onClick={playMix} className="!bg-white !text-primary-700 hover:!bg-white/90">
                  <Play className="mr-1.5 h-4 w-4" />Play Company Mix
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => openSearch()} className="!border-white/40 !bg-white/10 !text-white hover:!bg-white/20">
                <SearchIcon className="mr-1.5 h-4 w-4" />Explore the catalog
              </Button>
            </div>
          </div>
          {/* Featured recent (private) — the "jump back in" hook. */}
          {connected && featured && (
            <button type="button" onClick={() => playItem(featured)}
              className="group flex items-center gap-3 rounded-xl bg-white/10 p-2.5 pr-4 text-left backdrop-blur transition hover:bg-white/20">
              {(() => { const art = provider.artworkUrl(featured.artwork, 120); return art
                ? <img src={art} alt="" className="h-14 w-14 rounded-lg object-cover" />
                : <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-white/20"><Music2 className="h-6 w-6" /></span>; })()}
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-white/70">Jump back in</div>
                <div className="truncate text-sm font-semibold" dir="auto">{featured.title}</div>
                <div className="truncate text-xs text-white/80" dir="auto">{featured.artist || featured.subtitle}</div>
              </div>
              <span className="ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-primary-700 transition group-hover:scale-105"><Play className="h-4 w-4 translate-x-0.5" /></span>
            </button>
          )}
        </div>
      </section>

      {/* Mood shortcuts — user-customizable. Edit to add your own (e.g. Amapiano). */}
      <section>
        <SectionHead title="Browse by mood" action={
          <button type="button" onClick={() => setEditMoods((v) => !v)} className="text-xs font-medium text-primary-500 hover:underline">
            {editMoods ? 'Done' : 'Edit'}
          </button>
        } />
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
          {moods.map((m) => (
            <div key={m.label} className="relative">
              <button type="button" onClick={() => !editMoods && openSearch(m.query)}
                className={`flex h-16 w-full items-end rounded-xl bg-gradient-to-br ${gradientFor(m.label)} p-3 text-left text-sm font-semibold transition hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${editMoods ? 'cursor-default' : ''}`}>
                {m.label}
              </button>
              {editMoods && (
                <button type="button" onClick={() => removeMood(m.label)} aria-label={`Remove ${m.label}`}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background shadow">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={addMood}
            className="flex h-16 items-center justify-center gap-1 rounded-xl border border-dashed border-border text-sm font-medium text-muted-foreground transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </section>

      {connected && recentUnique.length > 0 && (
        <Section title="Recently played">
          <Carousel>
            {recentUnique.map((i) => <Tile key={i.id} item={i} onClick={() => playItem(i)} />)}
          </Carousel>
        </Section>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Company Mix — the workplace layer: generated from the team's most-loved tracks. */}
        <Section title="Company Mix">
          {mix && mix.tracks.length > 0 ? (
            <Card className="overflow-hidden">
              <CardContent className="pt-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500/10"><Flame className="h-6 w-6 text-primary-500" /></span>
                    <div>
                      <div className="font-semibold text-foreground">{mix.title}</div>
                      <div className="text-xs text-muted-foreground">{mix.subtitle} · {mix.track_count} songs</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowMixList((v) => !v)}>{showMixList ? 'Hide' : 'View'}</Button>
                    <Button size="sm" onClick={playMix}><Play className="mr-1.5 h-4 w-4" />Play</Button>
                  </div>
                </div>
                {showMixList && (
                  <ul className="mt-3 space-y-0.5 border-t border-border pt-3">
                    {mix.tracks.map((t, i) => (
                      <li key={`${t.canonical_url}-${i}`} className="flex items-center gap-3 rounded-lg p-2 hover:bg-muted">
                        <span className="w-5 text-center text-xs text-muted-foreground">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground" dir="auto">{t.title || t.canonical_url}</div>
                          <div className="truncate text-xs text-muted-foreground" dir="auto">{t.artist}</div>
                        </div>
                        <span className="text-xs text-muted-foreground">{t.votes} ♥</span>
                        <button type="button" aria-label="Open in provider" title="Open in provider" onClick={() => openProviderUrl(t.canonical_url)} className="rounded p-1 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><ExternalLink className="h-4 w-4" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ) : (
            <EmptyCard icon={<Flame className="h-6 w-6 text-primary-500" />} title="Your Company Mix is warming up"
              body="It builds from the songs your team votes for in challenges. Kick one off and it'll fill up." />
          )}
        </Section>

        {/* Active challenges — with a strong empty CTA so the surface never dead-ends. */}
        <Section title="Active challenges" action={challenges.length > 0 ? (
          <button onClick={onOpenChallenges} className="flex items-center text-xs font-medium text-primary-500 hover:underline">All challenges<ChevronRight className="h-3.5 w-3.5" /></button>
        ) : undefined}>
          {active.length > 0 ? (
            <div className="grid gap-3">
              {active.slice(0, 3).map((c) => (
                <button key={c.id} type="button" onClick={() => onOpenChallenge(c)}
                  className="rounded-xl border border-border bg-card p-4 text-left transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                  <div className="flex items-center gap-2 font-medium text-foreground"><Trophy className="h-4 w-4 text-primary-500" />{c.title}</div>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{c.participant_count ?? 0}</span>
                    {c.end_at && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtDate(c.end_at)}</span>}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <EmptyCard icon={<Trophy className="h-6 w-6 text-primary-500" />} title="No live challenges yet"
              body="Rally the team around a theme — Friday Energy, best focus track, team anthem — and let everyone vote."
              action={<Button size="sm" variant="outline" onClick={onOpenChallenges}><Plus className="mr-1.5 h-4 w-4" />Start a challenge</Button>} />
          )}
        </Section>
      </div>

      {trending.length > 0 && (
        <Section title="Trending in your company">
          <Card><CardContent className="space-y-1 pt-3">
            {trending.slice(0, 5).map((s, i) => (
              <div key={s.id} className="flex items-center gap-3 rounded-lg p-2 hover:bg-muted">
                <span className="w-5 text-center text-sm font-bold text-primary-500">{i + 1}</span>
                <ListMusic className="h-4 w-4 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{s.title || s.canonical_url}</div>
                  <div className="truncate text-xs text-muted-foreground">{s.artist || s.provider?.replace('_', ' ')}</div>
                </div>
                <span className="text-xs text-muted-foreground">{s.vote_count} votes</span>
              </div>
            ))}
          </CardContent></Card>
        </Section>
      )}

      {!connected && (
        <Card><CardContent className="flex flex-col items-center gap-2 py-8 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500/10"><Music2 className="h-6 w-6 text-primary-500" /></span>
          <div className="font-semibold text-foreground">Connect Apple Music to make it yours</div>
          <p className="max-w-sm text-sm text-muted-foreground">Your library and playback stay private — we never share your listening with your employer. Company challenges and the mix work either way.</p>
        </CardContent></Card>
      )}
    </div>
  );
};

const SectionHead: React.FC<{ title: string; action?: React.ReactNode }> = ({ title, action }) => (
  <div className="mb-2 flex items-center justify-between">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
    {action}
  </div>
);
const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <section>
    <SectionHead title={title} action={action} />
    {children}
  </section>
);
const EmptyCard: React.FC<{ icon: React.ReactNode; title: string; body: string; action?: React.ReactNode }> = ({ icon, title, body, action }) => (
  <Card className="border-dashed"><CardContent className="flex flex-col items-start gap-2 py-6">
    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/10">{icon}</span>
    <div className="font-semibold text-foreground">{title}</div>
    <p className="text-sm text-muted-foreground">{body}</p>
    {action && <div className="pt-1">{action}</div>}
  </CardContent></Card>
);
const Carousel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-3 overflow-x-auto pb-1">{children}</div>
);
const Tile: React.FC<{ item: MusicItem; onClick: () => void }> = ({ item, onClick }) => {
  const art = provider.artworkUrl(item.artwork, 160);
  return (
    <button type="button" onClick={onClick} className="group w-36 shrink-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
      <div className="relative h-36 w-36 overflow-hidden rounded-xl bg-muted">
        {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Music2 className="h-6 w-6 text-muted-foreground" /></div>}
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition group-hover:opacity-100"><Play className="h-7 w-7 text-white" /></span>
      </div>
      <div className="mt-1.5 truncate text-sm font-medium text-foreground" dir="auto">{item.title}</div>
      <div className="truncate text-xs text-muted-foreground" dir="auto">{item.artist || item.subtitle}</div>
    </button>
  );
};
